import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import { Controller, useFormContext } from 'react-hook-form-legacy';
import { FormGroup } from './FormGroup';
import InputError from './InputError';
import InputLabel from './InputLabel';
import ScrollToError from './ScrollToError';
import { getError } from './utils';

type Props = SwitchProps;

type SwitchProps = {
  label?: string;
  name: string;
  id?: string;
  checked?: boolean;
  placeholder?: string;
  children?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  className?: string;
  hint?: ReactNode;
  onChange?: (value: boolean) => void;
};

type ControlledSwitchProps = Omit<SwitchProps, 'label' | 'required'>;

export const ControlledSwitchInput: React.FC<
  React.PropsWithChildren<ControlledSwitchProps>
> = ({ name, id, disabled, className, onChange, checked, ...rest }) => {
  const {
    control,
    errors,
    formState: { isSubmitting },
  } = useFormContext();

  const handleChange = useCallback(
    (args: React.ChangeEvent<HTMLInputElement>[]) => {
      const [event] = args;
      onChange && onChange(event.target.checked);
      return event.target.checked;
    },
    [onChange],
  );

  const error = getError(name, errors);

  return (
    <>
      <Controller
        as={
          <Form.Check
            className={className}
            type="switch"
            id={id}
            disabled={disabled}
            isInvalid={!!error}
            readOnly={isSubmitting}
            checked={checked}
          />
        }
        onChange={handleChange}
        name={name}
        control={control}
        {...rest}
      />
    </>
  );
};

export const SwitchInput: React.FC<React.PropsWithChildren<SwitchProps>> = ({
  onChange,
  ...rest
}) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(event.target.checked);
    },
    [onChange],
  );

  return <Form.Check type="switch" onChange={handleChange} {...rest} />;
};

export const SwitchInputFormGroup: React.FC<React.PropsWithChildren<Props>> = ({
  id,
  label,
  name,
  required,
  hint,
  ...rest
}) => {
  const ref = useRef(null);
  const { errors } = useFormContext();

  const error = useMemo(() => getError(name, errors), [name, errors]);

  return React.useMemo(
    () => (
      <FormGroup ref={ref}>
        <Row>
          <Col>
            <InputLabel
              inputId={id}
              label={label}
              required={required}
              hint={hint}
              className="form-check-label"
            />
          </Col>

          <Col className="col-auto">
            <ControlledSwitchInput id={id} name={name} {...rest} />
          </Col>
        </Row>
        {error && (
          <Row>
            <Col>
              <InputError error={error} className="d-block" />
            </Col>
          </Row>
        )}
        <ScrollToError name={name} inputContainerRef={ref} />
      </FormGroup>
    ),
    [id, label, name, required, rest, ref, hint, error],
  );
};
